import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import MainLayout from "../../layouts/MainLayout"
import { getSales } from "../../services/salesService"
import {
  getBusinessLabel,
  getDashboardConfig,
} from "../../config/appConfig"


const DASHBOARD_SURFACE = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
  backgroundColor: "background.paper",
}

const DASHBOARD_FILTER_SURFACE = {
  ...DASHBOARD_SURFACE,
  p: { xs: 1.5, sm: 2 },
  mb: 2.5,
}

const DASHBOARD_SECTION_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
}

const COMMISSION_RATE = 0.05
const COMMISSION_RATE_LABEL = "5%"

const MAIN_COMPANY = getBusinessLabel("mainCompany", "Entreprise principale")
const MAIN_COMPANY_SHORT = getBusinessLabel("mainCompanyShort", MAIN_COMPANY)

const FIELD_AGENT = getBusinessLabel("fieldAgent", "Agent commercial")
const FIELD_AGENTS = getBusinessLabel("fieldAgents", "Agents commerciaux")

const COMPANY_PRICE = getBusinessLabel("companyPrice", "Prix entreprise")

const AGENT_DASHBOARD_CONFIG = getDashboardConfig("agents")

const AGENT_DASHBOARD_TITLE =
  AGENT_DASHBOARD_CONFIG?.label || `Dashboard ${FIELD_AGENTS.toLowerCase()}`

const AGENT_DASHBOARD_DESCRIPTION =
  AGENT_DASHBOARD_CONFIG?.description ||
  `Suivi des ${FIELD_AGENTS.toLowerCase()}, demandes terrain, paiements, livraisons et commissions.`

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatNumber(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })
}

function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("fr-FR")
}

function isAgentOpportunity(sale) {
  return (
    sale?.created_by_role === "agent_commercial" ||
    sale?.order_source === "agent_commercial"
  )
}

function getAgentStatusLabel(sale) {
  if (sale.status === "pending_price_validation") {
    return `À décider par ${MAIN_COMPANY_SHORT}`
  }

  if (
    sale.status === "pending" &&
    sale.payment_status === "unpaid"
  ) {
    return "Prix décidé / attente paiement"
  }

  if (
    sale.status === "pending" &&
    sale.payment_status === "paid"
  ) {
    return "Payée / à livrer"
  }

  if (
    sale.status === "confirmed" &&
    sale.payment_status === "paid"
  ) {
    return "Livrée"
  }

  if (sale.status === "cancelled") {
    return "Refusée"
  }

  return sale.status || "-"
}

function getAgentStatusColor(sale) {
  if (sale.status === "pending_price_validation") return "warning"

  if (
    sale.status === "pending" &&
    sale.payment_status === "paid"
  ) {
    return "info"
  }

  if (
    sale.status === "pending" &&
    sale.payment_status === "unpaid"
  ) {
    return "primary"
  }

  if (
    sale.status === "confirmed" &&
    sale.payment_status === "paid"
  ) {
    return "success"
  }

  if (sale.status === "cancelled") return "error"

  return "default"
}

function DashboardCard({ title, value, subtitle }) {
  return (
    <Card
      elevation={0}
      sx={{
        ...DASHBOARD_SURFACE,
        height: "100%",
        minHeight: 112,
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.075)",
        },
      }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>

        <Typography
          variant="h5"
          fontWeight={900}
          sx={{ mt: 0.5 }}
        >
          {value}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default function AgentCommercialDashboardPage() {
  const role = localStorage.getItem("kongosys_role") || ""
  const isAgentUser = role === "agent_commercial"

  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)

  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const loadData = async () => {
    try {
      setLoading(true)

      const params = {}

      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo

      const data = await getSales(params)

      const agentSales = Array.isArray(data)
        ? data.filter((sale) => isAgentOpportunity(sale))
        : []

      setSales(agentSales)
    } catch (error) {
      console.error(`Erreur dashboard ${FIELD_AGENTS.toLowerCase()} :`, error)

      showSnackbar(
        error?.response?.data?.detail ||
          `Impossible de charger le dashboard ${FIELD_AGENTS.toLowerCase()}.`,
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totals = useMemo(() => {
    return sales.reduce(
      (acc, sale) => {
        const total = toNumber(sale.total_amount)
        const paid = Math.min(
          total,
          toNumber(sale.confirmed_paid_amount ?? sale.paid_amount)
        )

        acc.totalRequests += 1
        acc.totalAmount += total
        acc.totalPaid += paid

        if (sale.customer_name) {
          acc.customers.add(sale.customer_name)
        }

        if (sale.status === "pending_price_validation") {
          acc.pendingDecision += 1
        }

        if (
          sale.status === "pending" &&
          sale.payment_status === "unpaid"
        ) {
          acc.priceDecided += 1
        }

        if (
          sale.status === "pending" &&
          sale.payment_status === "paid"
        ) {
          acc.paidWaitingDelivery += 1
        }

        if (
          sale.status === "confirmed" &&
          sale.payment_status === "paid"
        ) {
          acc.delivered += 1
          acc.deliveredAmount += total
        }

        if (sale.status === "cancelled") {
          acc.cancelled += 1
        }

        return acc
      },
      {
        totalRequests: 0,
        totalAmount: 0,
        totalPaid: 0,
        pendingDecision: 0,
        priceDecided: 0,
        paidWaitingDelivery: 0,
        delivered: 0,
        deliveredAmount: 0,
        cancelled: 0,
        customers: new Set(),
      }
    )
  }, [sales])

  const commissionEstimated = totals.deliveredAmount * COMMISSION_RATE

  const agentPerformance = useMemo(() => {
    const map = new Map()

    sales.forEach((sale) => {
      const agentName = sale.created_by_name || `${FIELD_AGENT} non renseigné`
      const total = toNumber(sale.total_amount)
      const paid = Math.min(
        total,
        toNumber(sale.confirmed_paid_amount ?? sale.paid_amount)
      )

      if (!map.has(agentName)) {
        map.set(agentName, {
          agentName,
          requests: 0,
          amount: 0,
          paid: 0,
          delivered: 0,
          deliveredAmount: 0,
          inProgress: 0,
          cancelled: 0,
        })
      }

      const row = map.get(agentName)

      row.requests += 1
      row.amount += total
      row.paid += paid

      if (
        sale.status === "confirmed" &&
        sale.payment_status === "paid"
      ) {
        row.delivered += 1
        row.deliveredAmount += total
      } else if (sale.status === "cancelled") {
        row.cancelled += 1
      } else {
        row.inProgress += 1
      }
    })

    return [...map.values()].sort((a, b) => b.amount - a.amount)
  }, [sales])

  const recentSales = useMemo(() => {
    return [...sales]
      .sort((a, b) => toNumber(b.id) - toNumber(a.id))
      .slice(0, 10)
  }, [sales])


  const agentPerformanceChartData = useMemo(() => {
    return agentPerformance
      .slice(0, 8)
      .map((row) => ({
        name: row.agentName,
        demandes: row.requests,
        livrees: row.delivered,
        montant: row.amount,
        commission: row.deliveredAmount * COMMISSION_RATE,
      }))
  }, [agentPerformance])


  const agentStatusChartData = useMemo(() => {
    return [
      {
        name: "À décider",
        value: totals.pendingDecision,
      },
      {
        name: "Prix décidé",
        value: totals.priceDecided,
      },
      {
        name: "Payées à livrer",
        value: totals.paidWaitingDelivery,
      },
      {
        name: "Livrées",
        value: totals.delivered,
      },
      {
        name: "Refusées",
        value: totals.cancelled,
      },
    ].filter((item) => item.value > 0)
  }, [totals])


  const topClientsChartData = useMemo(() => {
    const clients = new Map()

    sales.forEach((sale) => {
      const customerName =
        sale.customer_name ||
        "Client non renseigné"

      const current =
        clients.get(customerName) || {
          name: customerName,
          amount: 0,
          requests: 0,
        }

      current.amount +=
        toNumber(sale.total_amount)

      current.requests += 1

      clients.set(
        customerName,
        current
      )
    })

    return [...clients.values()]
      .sort(
        (a, b) =>
          b.amount - a.amount
      )
      .slice(0, 5)
  }, [sales])


  const chartColors = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
    "#7b1fa2",
    "#d32f2f",
  ]

  const resetFilters = () => {
    setDateFrom("")
    setDateTo("")
  }

  return (
    <MainLayout>
      <Box sx={{ maxWidth: "100%" }}>
        <Box sx={{ mb: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", fontWeight: 900, letterSpacing: 1.1 }}
          >
            PILOTAGE
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-end" }}
            spacing={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={900}
                sx={{ fontSize: { xs: "1.65rem", sm: "1.95rem", md: "2.1rem" } }}
              >
                {AGENT_DASHBOARD_TITLE}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, maxWidth: 900 }}>
                {isAgentUser
                  ? `Suivi de vos demandes terrain, paiements, livraisons et commission estimée.`
                  : AGENT_DASHBOARD_DESCRIPTION}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
              sx={{ minHeight: 44, flexShrink: 0 }}
            >
              Actualiser
            </Button>
          </Stack>
        </Box>

        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          La commission reste estimative. Elle devient réellement calculable
          après prix validé, paiement client et livraison confirmée.
        </Alert>

        <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr auto auto",
              },
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              label="Date début"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <TextField
              label="Date fin"
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <Button
              variant="contained"
              onClick={loadData}
              disabled={loading}
            >
              Charger
            </Button>

            <Button variant="outlined" onClick={resetFilters}>
              Reset
            </Button>
          </Box>
        </Paper>

        {loading ? (
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={26} />
              <Typography>Chargement...</Typography>
            </Stack>
          </Paper>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <DashboardCard
                title="Demandes"
                value={totals.totalRequests}
                subtitle="Opportunités créées"
              />

              <DashboardCard
                title="Montant total"
                value={formatAmount(totals.totalAmount)}
                subtitle="Prix validé ou système"
              />

              <DashboardCard
                title="À décider"
                value={totals.pendingDecision}
                subtitle={`Prix à valider par ${MAIN_COMPANY_SHORT}`}
              />

              <DashboardCard
                title="Prix décidé"
                value={totals.priceDecided}
                subtitle="Paiement attendu"
              />

              <DashboardCard
                title="Payées à livrer"
                value={totals.paidWaitingDelivery}
                subtitle="Sortie stock à confirmer"
              />

              <DashboardCard
                title="Livrées"
                value={totals.delivered}
                subtitle="Ventes confirmées"
              />

              <DashboardCard
                title="Clients touchés"
                value={totals.customers.size}
                subtitle="Clients amenés"
              />

              <DashboardCard
                title="Commission estimée"
                value={formatAmount(commissionEstimated)}
                subtitle={`${COMMISSION_RATE_LABEL} provisoire sur livrées`}
              />
            </Box>

            {/* =========================
                ANALYSE VISUELLE
                ========================= */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <Paper
                sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                >
                  Performance par {FIELD_AGENT.toLowerCase()}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Comparaison du nombre de demandes et des livraisons confirmées.
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    height: {
                      xs: 300,
                      sm: 330,
                    },
                  }}
                >
                  {agentPerformanceChartData.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        Aucune donnée disponible.
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <BarChart
                        data={agentPerformanceChartData}
                        margin={{
                          top: 10,
                          right: 15,
                          left: 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                        />

                        <XAxis
                          dataKey="name"
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          tick={{
                            fontSize: 11,
                          }}
                        />

                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                        />

                        <RechartsTooltip />

                        <Legend />

                        <Bar
                          dataKey="demandes"
                          name="Demandes"
                          fill="#1976d2"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={50}
                        />

                        <Bar
                          dataKey="livrees"
                          name="Livrées"
                          fill="#2e7d32"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={50}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Paper>


              <Paper
                sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                >
                  Répartition des demandes
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Situation actuelle des opportunités commerciales.
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    height: {
                      xs: 300,
                      sm: 330,
                    },
                  }}
                >
                  {agentStatusChartData.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        Aucune demande disponible.
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <PieChart>
                        <Pie
                          data={agentStatusChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="45%"
                          innerRadius={65}
                          outerRadius={100}
                          paddingAngle={2}
                          stroke="none"
                        >
                          {agentStatusChartData.map(
                            (item, index) => (
                              <Cell
                                key={`${item.name}-${index}`}
                                fill={
                                  chartColors[
                                    index % chartColors.length
                                  ]
                                }
                              />
                            )
                          )}
                        </Pie>

                        <RechartsTooltip
                          formatter={(value) => [
                            formatNumber(value),
                            "Demandes",
                          ]}
                        />

                        <Legend
                          verticalAlign="bottom"
                          height={44}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Paper>
            </Box>


            <Paper
              sx={{ ...DASHBOARD_SURFACE, p: 2, mb: 2, minWidth: 0 }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
              >
                Top 5 clients par montant
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Clients générant les montants commerciaux les plus élevés.
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  height: {
                    xs: 300,
                    sm: 330,
                  },
                }}
              >
                {topClientsChartData.length === 0 ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">
                      Aucun client disponible.
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={topClientsChartData}
                      layout="vertical"
                      margin={{
                        top: 10,
                        right: 20,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                      />

                      <XAxis
                        type="number"
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        type="category"
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        width={150}
                        tick={{
                          fontSize: 11,
                        }}
                      />

                      <RechartsTooltip
                        formatter={(value) => [
                          formatAmount(value),
                          "Montant",
                        ]}
                      />

                      <Bar
                        dataKey="amount"
                        name="Montant"
                        fill="#2e7d32"
                        radius={[0, 8, 8, 0]}
                        maxBarSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>


            <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, overflow: "hidden", mb: 2 }}>
              <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
                <Typography variant="h6" fontWeight="bold">
                  Performance par {FIELD_AGENT.toLowerCase()}
                </Typography>
              </Box>

              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{FIELD_AGENT}</TableCell>
                      <TableCell align="right">Demandes</TableCell>
                      <TableCell align="right">Montant</TableCell>
                      <TableCell align="right">Payé</TableCell>
                      <TableCell align="right">Livrées</TableCell>
                      <TableCell align="right">En cours</TableCell>
                      <TableCell align="right">Refusées</TableCell>
                      <TableCell align="right">Commission estimée</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {agentPerformance.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography color="text.secondary">
                            Aucune donnée {FIELD_AGENT.toLowerCase()}.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      agentPerformance.map((row) => (
                        <TableRow key={row.agentName} hover>
                          <TableCell>{row.agentName}</TableCell>

                          <TableCell align="right">{row.requests}</TableCell>

                          <TableCell align="right">
                            {formatAmount(row.amount)}
                          </TableCell>

                          <TableCell align="right">
                            {formatAmount(row.paid)}
                          </TableCell>

                          <TableCell align="right">{row.delivered}</TableCell>

                          <TableCell align="right">{row.inProgress}</TableCell>

                          <TableCell align="right">{row.cancelled}</TableCell>

                          <TableCell align="right">
                            {formatAmount(row.deliveredAmount * COMMISSION_RATE)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, overflow: "hidden" }}>
              <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
                <Typography variant="h6" fontWeight="bold">
                  Dernières demandes commerciales
                </Typography>
              </Box>

              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>{FIELD_AGENT}</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Lieu</TableCell>
                      <TableCell align="right">Montant</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {recentSales.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary">
                            Aucune demande commerciale trouvée.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentSales.map((sale) => (
                        <TableRow key={sale.id} hover>
                          <TableCell>{sale.sale_code || "-"}</TableCell>

                          <TableCell>{sale.created_by_name || "-"}</TableCell>

                          <TableCell>{sale.customer_name || "-"}</TableCell>

                          <TableCell>{sale.store_name || "-"}</TableCell>

                          <TableCell align="right">
                            {formatAmount(sale.total_amount)}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={getAgentStatusLabel(sale)}
                              color={getAgentStatusColor(sale)}
                              size="small"
                            />
                          </TableCell>

                          <TableCell>{formatDate(sale.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}